import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  DUPLICATE_JOB_ERROR,
  NO_CARD_ERROR,
  SHOW_CHILD_MISSING_TERMS_POPUP,
  SHOW_JOBS_INVITE_CONFIRMATION_POPUP,
  SHOW_MESSAGE,
  isOverlapingJob,
  showJobCreatePaymentError,
  showMessage,
} from '~/actions';
import { type IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { usePaymentEligibility } from '~/hooks/usePaymentEligibility';
import { clearAutoConfirm, hasAutoConfirmRequest } from '../adapters/resolveAutoConfirmRequest';
import { MultiDayShiftsService } from '../services';
import { type IPostShiftsResult } from '../types';

interface IPostShiftsMutationInput {
  formValues: IShiftDetailsFormValues;
  duplicate?: boolean;
}

interface IUsePostShiftsParams {
  officeId: string;
  allowNavigation: () => void;
}

export const usePostShifts = ({ officeId, allowNavigation }: IUsePostShiftsParams) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Deliberately NOT loading-guarded, unlike the two edit screens: a `true` must never survive
  // to submit because eligibility resolved late. The cost is the mirror case — a payable office
  // submitting on a cold payment query is stripped silently. Follow-up is to block submission
  // while eligibility is unknown, not to reverse this.
  const hasValidPaymentMethod = usePaymentEligibility(officeId).isEligible;

  /**
   * The values the request actually carries: an office with no payment method always posts with
   * Auto-Confirm off (GOTU-7076). Returned so the payload, the 453 attribution below, and the
   * review screen's analytics all read the same request.
   */
  const resolveSubmittedValues = (formValues: IShiftDetailsFormValues) =>
    hasValidPaymentMethod ? formValues : clearAutoConfirm(formValues);

  const mutation = useMutation({
    mutationFn: ({ formValues, duplicate }: IPostShiftsMutationInput) =>
      MultiDayShiftsService.postShifts(resolveSubmittedValues(formValues), officeId, duplicate),

    onSuccess: (result: IPostShiftsResult, { formValues }: IPostShiftsMutationInput) => {
      allowNavigation();
      navigate('/');

      // A new shift changes the dashboard counts and the open-shift list. Both
      // dashboard queries are cached, so invalidate them here to keep the tab
      // count and the shift cards in sync (matches cancel/confirm/decline).
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-jobs-info'] });

      if (result.hadInvites) {
        dispatch({
          type: SHOW_MESSAGE,
          payload: {
            title: 'Shifts Posted & Invites Successfully Sent!',
            message: React.createElement(
              'span',
              null,
              'Your shifts were sent to the GoTu community, and invited professionals received alerts for days they\u2019re available.',
              React.createElement('br'),
              'Note: Professionals won\u2019t receive invites for days in which they\u2019re unavailable.',
            ),
            isSuccess: true,
            buttonText: 'Close',
          },
        });
      } else if (formValues.autoConfirm.favorites || !hasValidPaymentMethod) {
        // Auto-confirm favorites, or an office that can't pay: in the latter case
        // inviting professionals is unavailable, so skip the invite recommendation and
        // show the plain success confirmation instead.
        dispatch(
          showMessage({
            title: 'Shift Posted Successfully!',
            message:
              'We\u2019re reaching out to all professionals in your area. Your shifts were sent successfully to the GoTu community. You will be notified when your offers have been accepted.',
            isSuccess: true,
          }),
        );
      } else {
        dispatch({
          type: SHOW_JOBS_INVITE_CONFIRMATION_POPUP,
          payload: {
            jobIds: result.response.jobs,
            profession: result.response.profession,
            officeId,
          },
        });
      }
    },

    onError: (
      error: AxiosError<{ message?: string }>,
      { formValues }: IPostShiftsMutationInput,
    ) => {
      const response = error?.response;
      const status = response?.status;
      const errorMessage: string = response?.data?.message || '';

      if (status === DUPLICATE_JOB_ERROR) {
        dispatch(isOverlapingJob(response?.data?.message));
      } else if (status === NO_CARD_ERROR) {
        // The API reuses this status for two unrelated conditions: auto-confirm requested
        // without payment, and an office past the no-payment post limit. Only the first is
        // about auto-confirm, and only we know whether we asked for it — so let the payload
        // decide, and otherwise surface the server's own explanation. Read the SUBMITTED values:
        // an office with no payment method never asks for auto-confirm.
        if (hasAutoConfirmRequest(resolveSubmittedValues(formValues))) {
          dispatch(showJobCreatePaymentError());
        } else {
          dispatch(
            showMessage({
              title: 'Sorry!',
              message: errorMessage || 'An unexpected error occurred.',
              isError: true,
              buttonText: 'Close',
            }),
          );
        }
      } else if (errorMessage.includes('Cannot post shifts')) {
        const popupMappings = [
          {
            substring: "GoTu's Policies",
            payload: { isTCMissing: true, isPSAMissing: true },
          },
          {
            substring: "GoTu's Terms and Conditions",
            payload: { isTCMissing: true, isPSAMissing: false },
          },
          {
            substring: "GoTu's Personnel Service Agreement",
            payload: { isTCMissing: false, isPSAMissing: true },
          },
        ];

        const matched = popupMappings.find(({ substring }) => errorMessage.includes(substring));

        if (matched) {
          dispatch({
            type: SHOW_CHILD_MISSING_TERMS_POPUP,
            payload: { ...matched.payload, officeId },
          });
        } else {
          dispatch(
            showMessage({
              title: 'Sorry!',
              message: errorMessage || 'An unexpected error occurred.',
              isError: true,
              buttonText: 'Close',
            }),
          );
        }
      } else {
        dispatch(
          showMessage({
            title: 'Sorry!',
            message: errorMessage || 'An unexpected error occurred.',
            isError: true,
            buttonText: 'Close',
          }),
        );
      }
    },
  });

  return { ...mutation, resolveSubmittedValues };
};
